import React, { type FC, memo, useMemo } from 'react';
import type { AwardPaymentRequest } from '../../types/awardPayment';
import { Card, CardContent } from '../Shared/Tailwind/card';
import { ACTIVE_REVIEW_STATUSES, toDisplayStatus } from './paymentRequestStatuses';
import { getActiveReviewStatusCounts } from './awardUtils';
import { getStatusColorClass } from '../../utils/awardStatusColor';

interface Props {
  requests: AwardPaymentRequest[];
  onSelectStatus?: (status: string) => void;
  selectedStatus?: string;
}

const CIRCLE_R = 15.91549430918954; // chosen so circumference === 100

const Tile: FC<{
  status: string;
  count: number;
  total: number;
  onSelectStatus?: (status: string) => void;
  selectedStatus?: string;
}> = ({ status, count, total, onSelectStatus, selectedStatus }) => {
  const percent = total > 0 ? (count / total) * 100 : 0;
  const percentLabel = `${Math.round(percent)}%`;
  const dash = `${percent} ${100 - percent}`;

  return (
    <Card
      key={status}
      className={`${`w-full h-[115px] max-w-sm ${selectedStatus === status ?'bg-gray-100':'bg-white'} border border-solid border-gray-300 py-0 overflow-visible shadow-[0_4px_12px_rgba(0,0,0,0.15),0_2px_4px_rgba(0,0,0,0.1)] hover:shadow-[0_6px_16px_rgba(0,0,0,0.2)] transition-shadow hover:cursor-pointer hover:bg-gray-50`}`}
      onClick={() => {onSelectStatus && onSelectStatus(status)}}
    >
      <CardContent className='flex items-center justify-between h-full p-4'>
        <div>
          <div className='text-lg font-bold text-text-primary text-left'>
            {toDisplayStatus(status)}
          </div>
          <div className='text-sm font-normal text-text-primary text-left'>
            {count} out of {total} requests
          </div>
          <div className='text-md font-bold text-text-secondary text-left'>
            {percentLabel}
          </div>
        </div>

        <svg
          width='60'
          height='60'
          viewBox='0 0 42 42'
          className='donut flex-shrink-0'
          role='img'
          aria-label={`${toDisplayStatus(status)}: ${percentLabel}`}
        >
          <circle className='donut-hole' cx='21' cy='21' r={CIRCLE_R} fill='#fff' />
          <circle
            className='donut-ring'
            cx='21'
            cy='21'
            r={CIRCLE_R}
            fill='transparent'
            stroke='rgba(255, 185, 70, 1)'
            strokeWidth='4'
          />
          <circle
            className={`donut-segment ${getStatusColorClass(status)}`}
            cx='21'
            cy='21'
            r={CIRCLE_R}
            fill='transparent'
            stroke='currentColor'
            strokeWidth='4'
            strokeDasharray={dash}
            strokeDashoffset='25'
          />
        </svg>
      </CardContent>
    </Card>
  );
};

const AwardSummaryTilesSection: FC<Props> = ({ requests , onSelectStatus, selectedStatus  }) => {
  const total = requests.length;

  const counts = useMemo(() => getActiveReviewStatusCounts(requests), [requests]);
  const statuses = useMemo(() => Array.from(ACTIVE_REVIEW_STATUSES), []);

  return (
    <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7 my-4'>
      {statuses.map((status) => (
        <Tile key={status} status={status} count={counts[status] ?? 0} total={total} onSelectStatus={onSelectStatus} selectedStatus={selectedStatus} />
      ))}
    </div>
  );
};

export default memo(AwardSummaryTilesSection);